<script setup>
import { ref, computed, onUnmounted } from 'vue';
import { useStore } from 'dashboard/composables/store';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

// Painel de ANDAMENTO das campanhas drip (WAHA / SMS / WhatsApp oficial com drip).
// Reusa o endpoint recipients ({ status: {...}, variants: {...} }) — sem backend
// novo. Funil cumulativo (enviados -> entregues -> lidos -> respondidos) + fila,
// falhas, descadastrados, A/B, alerta de auto-freio e estimativa de dias restantes.
// Atualiza sozinho a cada 8s enquanto aberto.
const store = useStore();

const dialogRef = ref(null);
const campaign = ref(null);
const data = ref(null); // { status: {...}, variants: {...} }
const loading = ref(false);
let timer = null;

const REFRESH_MS = 8000;

const fetchData = async () => {
  if (!campaign.value) return;
  loading.value = true;
  try {
    data.value = await store.dispatch(
      'campaigns/fetchRecipients',
      campaign.value.id
    );
  } catch (error) {
    // mantém os dados anteriores em caso de falha de rede
  } finally {
    loading.value = false;
  }
};

const open = async campaignObj => {
  campaign.value = campaignObj;
  data.value = null;
  dialogRef.value?.open();
  await fetchData();
  clearInterval(timer);
  timer = setInterval(fetchData, REFRESH_MS);
};

const stopPolling = () => {
  clearInterval(timer);
  timer = null;
};

onUnmounted(stopPolling);

const statusCounts = computed(() => data.value?.status || {});
const n = key => Number(statusCounts.value[key] || 0);

// Funil cumulativo: tudo que "saiu" conta como enviado; entregue >= lido >= respondido.
const replied = computed(() => n('replied'));
const read = computed(() => replied.value + n('read'));
const delivered = computed(() => read.value + n('delivered'));
const sent = computed(() => delivered.value + n('sent'));
const failed = computed(() => n('failed'));
const skipped = computed(() => n('skipped_optout') + n('skipped_invalid'));
const queue = computed(() => n('pending') + n('queued') + n('sending'));
const total = computed(
  () => sent.value + failed.value + skipped.value + queue.value
);
const processed = computed(() => total.value - queue.value);
const progressPct = computed(() =>
  total.value === 0 ? 0 : Math.round((processed.value / total.value) * 100)
);

// Estimativa de dias restantes: fila ÷ cota diária.
const dailyLimit = computed(
  () => Number(campaign.value?.drip_config?.daily_limit) || 0
);
const remainingDays = computed(() =>
  dailyLimit.value > 0 ? Math.ceil(queue.value / dailyLimit.value) : null
);

const dripStatus = computed(() => campaign.value?.drip_status);
const brakeReason = computed(() =>
  dripStatus.value === 'paused' ? campaign.value?.drip_config?.brake_reason : null
);

const funnel = computed(() => [
  { label: 'Enviados', value: sent.value, cls: 'text-n-slate-12' },
  { label: 'Entregues', value: delivered.value, cls: 'text-n-teal-11' },
  { label: 'Lidos', value: read.value, cls: 'text-n-blue-11' },
  { label: 'Respondidos', value: replied.value, cls: 'text-n-teal-11' },
]);

const secondary = computed(() => [
  { label: 'Na fila', value: queue.value, cls: 'text-n-amber-11' },
  { label: 'Falhas', value: failed.value, cls: 'text-n-ruby-11' },
  { label: 'Descadastrados', value: skipped.value, cls: 'text-n-slate-11' },
  { label: 'Total', value: total.value, cls: 'text-n-slate-12' },
]);

const variants = computed(() => Object.entries(data.value?.variants || {}));

// Ativação por palavra-chave (SMS -> WhatsApp): só vem preenchido quando a campanha
// usa esse recurso. responded/positive/contacted + a lista de quem já foi contatado.
const keywordActivation = computed(() => data.value?.keyword_activation || null);
const kaStats = computed(() => {
  const ka = keywordActivation.value;
  if (!ka) return [];
  return [
    { label: 'Responderam', value: ka.responded || 0, cls: 'text-n-blue-11' },
    { label: 'Positivas', value: ka.positive || 0, cls: 'text-n-teal-11' },
    { label: 'Contatadas no WhatsApp', value: ka.contacted || 0, cls: 'text-n-teal-11' },
  ];
});
const kaContacts = computed(() => keywordActivation.value?.contacts || []);
const formatTime = iso => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch (e) {
    return '';
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="`Andamento — ${campaign?.title || ''}`"
    :show-confirm-button="false"
    :cancel-button-label="'Fechar'"
    width="xl"
    @close="stopPolling"
  >
    <div class="flex flex-col gap-4">
      <!-- barra de progresso -->
      <div class="flex flex-col gap-1.5">
        <div class="flex items-center justify-between text-sm">
          <span class="font-medium text-n-slate-12">
            {{ processed }} de {{ total }} processados
          </span>
          <span class="text-n-slate-11">{{ progressPct }}%</span>
        </div>
        <div class="w-full h-2 rounded-full bg-n-alpha-2 overflow-hidden">
          <div
            class="h-full bg-n-teal-9 transition-all duration-500"
            :style="{ width: `${progressPct}%` }"
          />
        </div>
      </div>

      <!-- funil de entrega -->
      <div class="grid grid-cols-4 gap-2">
        <div
          v-for="item in funnel"
          :key="item.label"
          class="flex flex-col gap-0.5 p-2 rounded-xl bg-n-alpha-2 border border-n-weak"
        >
          <span class="text-lg font-medium" :class="item.cls">{{ item.value }}</span>
          <span class="text-xs text-n-slate-10">{{ item.label }}</span>
        </div>
      </div>

      <!-- fila / falhas / descadastrados / total -->
      <div class="grid grid-cols-4 gap-2">
        <div
          v-for="item in secondary"
          :key="item.label"
          class="flex flex-col gap-0.5 p-2 rounded-xl bg-n-alpha-2 border border-n-weak"
        >
          <span class="text-lg font-medium" :class="item.cls">{{ item.value }}</span>
          <span class="text-xs text-n-slate-10">{{ item.label }}</span>
        </div>
      </div>

      <!-- estimativa de dias restantes -->
      <p
        v-if="dripStatus === 'running' && remainingDays !== null && queue > 0"
        class="text-xs text-n-slate-11"
      >
        Faltam ~{{ remainingDays }} dia(s) para concluir (cota de
        {{ dailyLimit }}/dia).
      </p>

      <!-- teste A/B -->
      <div v-if="variants.length" class="flex flex-col gap-1.5">
        <span class="text-sm font-medium text-n-slate-12">Teste A/B</span>
        <div class="grid grid-cols-2 gap-2">
          <div
            v-for="[name, count] in variants"
            :key="name"
            class="flex items-center justify-between p-2 rounded-xl bg-n-alpha-2 border border-n-weak text-sm"
          >
            <span class="text-n-slate-11">{{ name }}</span>
            <span class="font-semibold text-n-slate-12">{{ count }}</span>
          </div>
        </div>
      </div>

      <!-- ativação por palavra-chave (SMS -> WhatsApp) -->
      <div v-if="keywordActivation" class="flex flex-col gap-1.5">
        <span class="text-sm font-medium text-n-slate-12">
          Ativação por palavra-chave (SMS → WhatsApp)
        </span>
        <div class="grid grid-cols-3 gap-2">
          <div
            v-for="item in kaStats"
            :key="item.label"
            class="flex flex-col gap-0.5 p-2 rounded-xl bg-n-alpha-2 border border-n-weak"
          >
            <span class="text-lg font-medium" :class="item.cls">{{ item.value }}</span>
            <span class="text-xs text-n-slate-10">{{ item.label }}</span>
          </div>
        </div>
        <div v-if="kaContacts.length" class="flex flex-col gap-1 mt-0.5">
          <span class="text-xs font-medium text-n-slate-11">Contatados no WhatsApp</span>
          <div class="max-h-40 overflow-y-auto rounded-xl border border-n-weak divide-y divide-n-weak">
            <div
              v-for="(c, idx) in kaContacts"
              :key="idx"
              class="flex items-center gap-2 px-2 py-1.5 text-xs"
            >
              <span class="text-n-slate-12 font-medium truncate flex-1">
                {{ c.name || c.phone }}
              </span>
              <span class="text-n-slate-10 font-mono shrink-0">{{ c.phone }}</span>
              <span
                v-if="c.keyword"
                class="px-1.5 py-0.5 rounded bg-n-teal-3 text-n-teal-11 shrink-0"
              >
                {{ c.keyword }}
              </span>
              <span class="text-n-slate-10 shrink-0">{{ formatTime(c.at) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- alerta de auto-freio -->
      <div
        v-if="brakeReason"
        class="flex items-start gap-1.5 p-2 rounded-xl bg-n-amber-3 text-xs text-n-amber-11"
      >
        <span class="i-ri-alert-line shrink-0 mt-0.5" />
        <span>Pausada automaticamente — {{ brakeReason }}. Revise o número e clique em Retomar.</span>
      </div>

      <div class="flex items-center gap-2 text-xs text-n-slate-10">
        <Spinner v-if="loading" class="size-3" />
        <span>Atualiza automaticamente a cada 8s.</span>
      </div>
    </div>
  </Dialog>
</template>
